/**
 * Copyright IBM Corp. 2022
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from "react";
import PropTypes from "prop-types";

const cssBase = "triBimViewer";
const FORGE_PATH = "/p/components/r/1/v/en-US/l/forge-viewer/ForgeViewer.html";


export default class TriBimViewer extends React.PureComponent
{
  static propTypes = {
    /**
    * Override for the REST URL used to authenticate with the Autodesk Forge service. 
    */
    authurl: PropTypes.string,
    /**
    * Controls auto zoom on select behavior. 
    */
    autozoom: PropTypes.boolean,
    /**
     * CSS classs for viewer iFrame
     */
    className:   PropTypes.string,
    /**
     * Override of the context root used for the Forge service Auth REST call.
     */
    contextroot: PropTypes.string,
    /**
      * A list of features expressed as toolbar icons to enable or disable.  Supported values are:
      * - camera      : Autodesk camera submenu
      *                 Default = true
      * - explode     : Autodesk explode tool.
      *                 Default = false
      * - fullscreen  : Autodesk viewer full screen toggle
      *                 Default = true
      * - markup      : Loads the markup extensions and displayes markup tools on the toolbar
      *                 Default = false
      * - measure     : Autodesk measure tool.
      *                 Default = false
      * - modelTree   : Autodesk Model Tree viewer
      *                 Default = true
      * - multiselect : TRIRIGA single select/multi select toggle.
      *                 Default = false
      * - properties  : AUtodesk pproperty viewer
      *                 Default = true
      * - search      : TRIRIGA search bar
      *                 Default = true
      * - section     : TRIRIGA selection submenu: Isolate Selectiom, Hide Selection, Auto Zoom, MUlti-select
      *                 Default = true
      * - selection   : TRIRIGA selection submenu
      *                 Default = true
      * - settings    : Autodesk viewer settings.
      *                 Default = false
      * - view        : Displays the save and restore view tools on the toolbar.
      *                 Default = false
      */
    features: PropTypes.string,
    /**
      * Override for the URL for the src value of the viewer iFrame.  It should be the 
      * path to ForgeViewer.html.
      */
    frameurl: PropTypes.string,
    /**
      * Viewerheight, default to 100%.
      */
    height: PropTypes.string,
    /**
      * One of:
      *     Chinese Simplified: zh-cn
      *     Chinese Traditional: zh-tw
      *     Czech: cs
      *     English: en
      *     French: fr
      *     German: de
      *     Italian: it
      *     Japanese: ja
      *     Korean: ko
      *     Polish: pl
      *     Portuguese Brazil: pt-br
      *     Russian: ru
      *     Spanish: es
      *     Turkish: tr
      * If it is unset, the viewer attempts to determin the locale from thge browser context
      */
    locale: PropTypes.string,
    /**
      * For non-Revit models: The name of the model property that contains the unique ID used to bind
      * application data to model elements. 
      */
    mapping: PropTypes.string,
    /**
     * The triBIMModelLink BO linking building to model URN and thumbnail. 
     *	- binding:       Model specific instructions to the viewer. Used by the Forge viewer and NavisWorks models to specify the NavisWOrks attribute that has the uniqueId.
     *	- buildingId:    TRIRIGA building Id 
     *	- description
     *	- displayOrder:  When linking multiple models ro a single building is supported, it will specify sort order of the list of models
     *	- linkType:      The name of the BO linked to this record. Currently only triBuilding is supported.
     *	- objectKey:     The Forge service Object key, May be redefined by other viewers
     *	- thumbnail:     URN of model thumbnail
     *	- title:         Title to use when displaying the model definition to a user.
     *	- urn: 		     URN required to launch the BIM viewer with the model associated with the thumbnail 
     *	- viewer:        Name of the viewer to used to display the model.  Currently only Forge is supported
     *	- _bldgSpec:     TRIRIGA Spec ID (_id) of the associated building
     *	- _id:           TRIRIGA ID of this record
     */
    model: PropTypes.string,
    /**
     * Callback for reporting vewer errors in REST calls
     */
    onRESTError: PropTypes.func,
    /**
     * Called when the selected element in the model changes, or the focus element in
     * the list of selected elements changes
     * - ctrl:          The viewr control.  Can be used for viewer API calls
     * - selectionList: The list of all selected items
     * - selection:     The item in the selection list with focus
     * - count:         Number of items in the selection list
     * - index:         Index of the focus item in the selectioh list
     */
    onSelectionChange: PropTypes.func,
    /**
     * Record ID of the business process record that sets the viewer context.  For example, a
     * work task record.  This is used for associating markup with the context.  Its value is opaque
     * to the viewer.  When markup is created, it is associated with this value.  When it is 
     * displayed, only records that match this context are displayed.
     */
    recordkey: PropTypes.string,
    /*
     * This is an opaque string created by viewer vendor specific tools which instructs the
     * underling viewer to load a specific model; For the Forege viewer, this is the Forge
     * viewer service URN
     */
    urn: PropTypes.string,
    /*
     * The GUID of a model element.  For the Forege viewer, this is the Autodesk Export GUID
     */
    value: PropTypes.string,
    /**
      * Name of the viewer implementation to use to display a specific model 
      * Ex: "Forge" for the Autodesk Forge viewer
      */
    viewer: PropTypes.string,
    /**
      * Viewer width, default to 100%
      */
    width: PropTypes.string,
    /**
     * If true, the viewer zooms to the center of the object.  This typically places the
     * camera inside the object.  Use for rooms
     */
    zoomToCenter: PropTypes.boolean,
  }

    static VIEWER_FRAME_ID = "triBimViewerFrame";

    constructor( props )
    {
        super( props );

        this.viewerInit = false;
        this.viewerWrapper = null;
        this._activeURN = "";

        this.state = {};

        if( !this.props.contextroot )
        {
            this.contextroot = "";
            const path = location.pathname;
            const idx = path.lastIndexOf( "/app/" );
            if( idx >= 0 )
            {
                this.contextroot = path.substring( 0, idx );
            }
        }
        else
        {
            this.contextroot = this.props.contextroot
        }

        if( !this.props.frameurl || this.props.frameurl == "" )
        {
            if( this.importPath )
            {
                this.state.frameurl = this.importPath + "ForgeViewer.html";
            }
            else
            {
              this.state.frameurl = this.contextroot + FORGE_PATH;
            }
        }
    }

    componentDidMount()
    {
      this.forceSizeforIOS();
    }

    render()
    {
        if( this.props.model !== this.lastModel )
        {
            this.lastModel = this.props.model;
            this.handleSelectModel( this.props.model );
        }
        else if( this.props.urn !== this._activeURN )
        {
            const model = {
                binding:  this.props.mapping,
                mboKey:   this.props.recordkey,
                urn:      this.props.urn,
                viewer:   this.props.viewer,
                autozoom: this.props.autozoom,
            }
            this.handleSelectModel( model );
        }

      if( this.props.value !== this.lastValue )
      {
        this.lastValue = this.props.value;
        this.handleSelectElement( this.props.value );
      }

        const { className } = this.props;

        const classes = cssBase + ( className ? " " + className : "" );

        const forgeIFrameProps = {
            id: TriBimViewer.VIEWER_FRAME_ID,
            height: this.props.height,
            width: this.props.width,
            marginWidth: "0",
            marginHeight: "0",
            frameBorder: "0",
            scrolling:    "no",
            allowFullScreen: "true",
            webkitallowfullscreen: "true",
            mozallowfullscreen: "true",
            onLoad: ( evt ) => { this.handleViewerFrameLoad( evt ) }
        }

        return <iframe
            className={ classes }
            { ...forgeIFrameProps }
            src={ this.state.frameurl }
        >
        </iframe>
    }


    // Called when iFrame source is loaded
    handleViewerFrameLoad( evt )
    {
        this.viewerInit = false;
        this.frame = evt.currentTarget;

        setTimeout( () =>
        {
            this.loadWrapper( this.frame );
        }, 100 );
    }

    loadWrapper()
	{
        if( this.viewerInit ) return;
        if( !this.frame.contentWindow ) return;
        if( !this.frame.contentWindow.loadWrapper ) return;

        this.viewerInit = true;
        this.contentWindow = this.frame.contentWindow;

        this.frame.contentWindow.loadWrapper(
            this.props.viewer,
            () => { this.initializeViewer( this.frame.contentWindow ); }
        );
    }

    initializeViewer( contentWindow )
    {
        let zoomMode = false;
        if( this.props.autozoom )
        {
            zoomMode = this.props.zoomToCenter ? "center" : true;
        }
        const modelMgr = contentWindow.initModelManager(
        ( ctrl, selectionList, selection, count, index ) =>
            {
                this.handleSelectionChanged( ctrl, selectionList, selection, count, index );
            },
            this.props.locale,
            zoomMode,
            this.props.features,
            this.contextroot
        );

        this.viewerWrapper = modelMgr.viewer;
        this.viewerWrapper.setErrorHandlers(
            ( msg ) => { this._displayError( msg ); },
            ( status, source, responseText, component ) =>
            {
                this.handleRESTError( status, source, responseText, component );
            }
        );

        if( this.props.authurl )
        {
            this.viewerWrapper.setRestURL(
                this.contextroot,
                this.contextroot + this.props.authurl + "/auth/token" );
        }

        const model = this.props.model;
        if( model && model.urn && model.urn != "" )
        {
            this.viewerWrapper.setCurrentModel( model );
            this.viewerWrapper.loadFile( model.urn, model.binding );
            if( this.props.value )
            {
                this.handleSelectElement( this.props.value );
            }
        }
        this.viewerWrapper.setAutoZoom( this.props.autozoom );
        this.viewerWrapper.onFakeFullScreen = ( mode ) => { this.fakeFullScreen( mode ); };
    }

    // This is a HACK! to working around the following iOS issue:
    // https://github.com/mrdoob/three.js/issues/9195
    // If assumes that th view extends to the bottom and right of the screen
    forceSizeforIOS()
    {
        const userAgent = window.navigator.userAgent;
        if( !userAgent.match( /iPad/i ) && !userAgent.match( /iPhone/i ) ) 
        {
          return;
        }

        let node = this.frame;
        let x = 0;
        let y = 0;
        while( node != null )
        {
          if( node.offsetTop ) y += node.offsetTop;
          if( node.offsetLeft ) x += node.offsetLeft;
          if( node.offsetParent != null )
          {
            node = node.offsetParent;
          }
          else
          {
            node = node.parentNode;
          }
        }
        const h = window.innerHeight - y;
        const w = window.innerWidth - x;
        if( this.frame.height == "100%" || this.forceHeight )	
        {
          this.frame.height = "" + h + "px";
          this.forceHeight = true;
        }
        if( this.frame.width == "100%" || this.forceWidth )	
        {
          this.frame.width = "" + w + "px";
          this.forceWidth = true;
        }
    }

    fakeFullScreen( mode )
    {
        if( mode )
        {
            this.savePosition = this.frame.style.position;
            this.saveTop = this.frame.style.top;
            this.saveLeft = this.frame.style.left;

            this.frame.style.position = "absolute";
            this.frame.style.top = 0;
            this.frame.style.left = 0;
            this.frame.style.zIndex = 1000;
        }
        else
        {
            this.frame.style.position = this.savePosition;
            this.frame.style.top = this.saveTop;
            this.frame.style.left = this.saveLeft;
        }
        this._forceSizeforIOS();
    }

    handleSelectModel( model )
    {
        if( !model || model.urn === this._activeURN ) return;

        if( !model.mboKey )
        {
            model.mboKey = this.props.recordkey;
        }

        // See if iFrame is fullly loaded, if not, wait on load event
        if(    this.contentWindow
            && this.contentWindoww.ViewerWrapper )
        {
            if( !this.viewerWrapper )
            {
                this.loadWrapper();
                return;
            }

            this._activeURN = model.urn;

            if( !model || !model.urn || model.urn == "" )
            {
                this.viewerWrapper.setCurrentModel( null );
                this.viewerWrapper.loadFile( "" );
                return;
            }
            this.viewerWrapper.setCurrentModel( model );
            this.viewerWrapper.loadFile( model.urn, model.binding );

            if( this.props.value )
            {
                this.viewerWrapper.selectValue( this.props.value, this.props.autozoom );
            }
        }
    }

    handleSelectElement( GUID )
    {
        if( this.viewerWrapper )
        {
            let zoomMode = false;
            if( this.props.autozoom )
            {
                zoomMode = this.props.zoomToCenter ? "center" : true;
            }
            this.viewerWrapper.selectValue( GUID, zoomMode );
        }
        else
        {
            this.deferredValue = GUID;
        }
    }

    handleRESTError( status, source, responseText, component )
    {
        if( this.props.onRESTError )
        {
            this.props.onRESTError( status, source, responseText, component );
        }
    }

    handleSelectionChanged( ctrl, selectionList, selection, count, index )
    {
        if( this.props.onSelectionChange )
        {
            this.props.onSelectionChange( ctrl, selectionList, selection, count, index );
        }
    }
}

TriBimViewer.defaultProps = {
    autozoom: "center",
    authurl: "/api/p/v1/forge",
    height: "100%",
    locale: "en",
    viewer: "Forge",
    width: "100%",
};
